"use client";

import { useEffect, useRef, useState, Suspense } from "react";
import Image from "next/image";
import { gsap } from "gsap";

// ✅ Skeleton Component
function HeroSkeleton() {
  return (
    <section className="relative overflow-hidden min-h-screen flex flex-col md:flex-row justify-between items-center px-8 md:px-20 py-16  animate-pulse">
      {/* Left Section */}
      <div className="flex-1 flex justify-center items-center">
        <div className="w-80 h-80 bg-gray-300 rounded-2xl" />
      </div>

      {/* Right Section */}
      <div className="flex-1 flex flex-col gap-4">
        <div className="w-48 h-6 bg-gray-300 rounded"></div>
        <div className="w-72 h-10 bg-gray-300 rounded"></div>
        <div className="w-64 h-8 bg-gray-300 rounded"></div>
        <div className="w-96 h-5 bg-gray-300 rounded mt-4"></div>

        <div className="mt-6 space-y-3">
          {Array(4)
            .fill(0)
            .map((_, i) => (
              <div key={i} className="w-[300px] h-8 bg-gray-300 rounded-full"></div>
            ))}
        </div>
      </div>
    </section>
  );
}

// ✅ Actual Hero Section
function HeroContent() {
  const heroRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // Preload all images
  useEffect(() => {
    const images = [
      "/bg-v-k.jpg",
      "/Para_logo.webp",
      "/100g.png",
      "/dsp1.png",
      "/stage.png",
    ];

    Promise.all(
      images.map(
        (src) =>
          new Promise((resolve) => {
            const img = new window.Image();
            img.src = src;
            img.onload = () => resolve(true);
          })
      )
    ).then(() => setLoaded(true));
  }, []);

  // Animate after loading
  useEffect(() => {
    // if (!loaded || !heroRef.current) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from(".hero-left", { x: -100, opacity: 0, duration: 1 })
        .from(".hero-right", { x: 100, opacity: 0, duration: 1 }, "-=0.6")
        .from(".feature", { y: 40, opacity: 0, duration: 0.8, stagger: 0.15 }, "-=0.4");
    }, heroRef);

    return () => ctx.revert();
  }, [loaded]);

  if (!loaded) return <HeroSkeleton />;

  return (
    <section
      ref={heroRef}
      className="relative overflow-hidden min-h-screen  flex flex-col md:flex-row justify-between items-center px-8 md:px-20 py-16 "
    >
      {/* ✅ Background Image */}
      <div className="absolute bg-white inset-0 -z-10">
        <Image
          src="/bg-v-k.jpg"
          alt="Background"
          fill
          priority
          className="object-cover lg:object-contain object-bottom transition-opacity duration-700"
        />
      </div>

      {/* ✅ Decorative Wrapper */}
      <div className="absolute hidden lg:block inset-0 z-0 overflow-hidden">
        <Image
          src="/Para_logo.webp"
          alt="Para Logo"
          width={300}
          height={300}
          className=" absolute   top-0 left-0   object-contain opacity-90"
          loading="eager"
        />
        <Image
          src="/100g.png"
          alt="Guarantee Seal"
          width={180}
          height={180}
          className="absolute  right-10 top-10  object-contain"
          loading="eager"
        />
      </div>

      {/* ✅ Left Product Section */}
      <div className="hero-left relative flex-1 flex justify-center items-center z-10">
        <div className="absolute -left-10 top-1/2 -rotate-90 text-3xl tracking-widest lg:text-gray-600 font-semibold select-none">
          PARA INNOVATION
        </div>

        <div className="relative z-10 flex items-center justify-center">
          <Image
            src="/dsp1.png"
            alt="Kisan Mithraa Product"
            width={320}
            height={320}
            className="rounded-2xl object-contain"
            loading="eager"
          />
        </div>

        <Image
          src="/stage.png"
          alt="Stage"
          width={520}
          height={520}
          className="absolute mb-10 lg:mb-0 -bottom-20 w-96 object-contain"
          loading="eager"
        />
      </div>

      {/* ✅ Right Info Section */}
      <div className="hero-right lg:mt-0 mt-10 flex-1 flex flex-col items-start gap-4 text-gray-800 z-10">
        <h2 className="text-2xl font-semibold tracking-wider text-white lg:text-gray-600">
          PARA INNOVATION
        </h2>
        <h1 className="text-6xl text-white lg:text-black font-bold tracking-wide">KISAN MITHRAA</h1>
        <h1 className="text-3xl font-semibold text-white lg:text-gray-700 mb-4">
          MOBILE STARTER
        </h1>
        <p className="text-lg font-medium text-white lg:text-gray-600">
          Mobile Starter: Effortless Motor Control at Your Fingertips
        </p>

        <div className="mt-6 space-y-3">
          {[
            "DRY RUN PROTECTION",
            "OVER LOAD PROTECTION",
            "CALL / SMS CONTROL",
            "24/7 CUSTOMER SUPPORT",
          ].map((text, i) => (
            <div
              key={i}
              className="feature flex justify-between items-center bg-[#e5e7e4] px-4 py-2 rounded-full text-sm font-medium shadow-sm border border-gray-200 w-[300px]"
            >
              <span>{text}</span>
              <span className="text-gray-400 font-extrabold tracking-widest">
                •••
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ✅ Suspense Wrapper
export default function HeroSection() {
  return (
    <Suspense fallback={<HeroSkeleton />}>
      <HeroContent />
    </Suspense>
  );
}
